/**
 * 정산년월 선택기. 정산 화면 넷이 같은 것을 쓴다(청구·명세·정산현황 / 재무추가 / 월정기 / 영업정산).
 *
 * 고른 달은 **기억된다**(`lib/driveMon.ts`). 그래서 이 선택기는 최신 월이 아닐 때
 * **그렇다고 말해 준다** — 기억은 조용히 남으면 나중에 "왜 이 달이 뜨지"를 아무도 못 푼다.
 */
export default function DriveMonPicker({
  months,
  value,
  onChange,
  extra,
  extraAt = 'first',
  head,
  ahead,
  width = 'w-36',
}: {
  months: readonly string[];
  value: string;
  onChange: (m: string) => void;
  /** 월이 아닌 선택지 — 재무추가의 `이월잔액`(`drive_mon=''`). 기억 대상이 아니다. */
  extra?: { value: string; label: string };
  /** 원래 화면의 목록 순서를 지킨다. 익숙한 자리에서 옮기지 않는다. */
  extraAt?: 'first' | 'last';
  /** 맨 위 선택지 — 재무추가의 `전체`(정산월 전체 · #183). `extra` 와 같이 기억 대상이 아니다. */
  head?: { value: string; label: string };
  /**
   * 최신 달 **뒤**의 달(#511 · 재무추가에 미리 넣어 둔 2027-01 등). 맨 위에 보이되 「최신」은 그대로 `months[0]` —
   * 고르면 「기억됨 · 최신」 안내가 떠서 미래 달을 보고 있다는 게 드러난다.
   */
  ahead?: readonly string[];
  width?: string;
}) {
  const latest = months[0] ?? '';
  // `extra`·`head` 가 골라진 상태는 "지난 달을 보고 있다"가 아니라 다른 축이다 — 안내를 띄우지 않는다.
  const stale = latest !== '' && value !== '' && value !== latest && value !== extra?.value && value !== head?.value;

  return (
    <div className="flex shrink-0 items-center gap-2">
      {/* 폰에선 글자를 뺀다 — 선택기 값(2026-09)만으로 뜻이 보이고 한 줄 폭이 아깝다. */}
      <label className="hidden whitespace-nowrap text-sm text-ink-soft sm:inline">정산년월</label>
      <select className={`field ${width}`} value={value} onChange={(e) => onChange(e.target.value)}>
        {head && <option value={head.value}>{head.label}</option>}
        {extra && extraAt === 'first' && <option value={extra.value}>{extra.label}</option>}
        {ahead?.map((m) => (
          <option key={`a${m}`} value={m}>
            {m} (미리 넣은 달)
          </option>
        ))}
        {months.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
        {extra && extraAt === 'last' && <option value={extra.value}>{extra.label}</option>}
      </select>
      {stale && (
        <button
          type="button"
          className="whitespace-nowrap rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
          title="고른 정산년월은 화면을 옮겨도 그대로 남습니다. 누르면 최신 월로 돌아갑니다."
          onClick={() => onChange(latest)}
        >
          <span className="hidden sm:inline">기억됨 · </span>최신<span className="hidden sm:inline"> {latest}</span>
        </button>
      )}
    </div>
  );
}
